import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { useAuth } from '../../../../contexts/AuthContext';
import { 
  getAllContent, 
  createContent, 
  updateContent, 
  deleteContent, 
  CMSContent 
} from '../../../../firebase/services/cmsService';

const ContentPage: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState<CMSContent[]>([]);
  const [filteredContent, setFilteredContent] = useState<CMSContent[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<Partial<CMSContent> | null>(null);
  const [isNewContent, setIsNewContent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadContent();
  }, []);

  useEffect(() => {
    filterContent();
  }, [content, searchTerm, categoryFilter, typeFilter]);

  const loadContent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const allContent = await getAllContent();
      setContent(allContent);
      
    } catch (err: any) {
      console.error('Error loading content:', err);
      setError(err.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const filterContent = () => {
    let filtered = content;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(item => item.type === typeFilter);
    }

    setFilteredContent(filtered);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddContent = () => {
    setEditingContent({
      key: '',
      title: '',
      content: '',
      type: 'text',
      category: '',
      isActive: true,
    });
    setIsNewContent(true);
    setEditDialogOpen(true);
  };

  const handleEditContent = (item: CMSContent) => {
    setEditingContent({ ...item });
    setIsNewContent(false);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (item: CMSContent) => {
    setEditingContent(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!editingContent?.id) return;

    try {
      await deleteContent(editingContent.id);
      setContent(prev => prev.filter(item => item.id !== editingContent.id));
      setDeleteDialogOpen(false);
      setEditingContent(null);
      setSuccess('Content deleted successfully!');
    } catch (err: any) {
      console.error('Error deleting content:', err);
      setError(err.message || 'Failed to delete content');
    }
  };

  const handleSaveContent = async () => {
    if (!editingContent) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const userId = userProfile?.uid || 'unknown';
      const now = Date.now();

      if (isNewContent) {
        const newContent = {
          ...editingContent,
          createdBy: userId,
          updatedBy: userId,
        } as Omit<CMSContent, 'id' | 'createdAt' | 'updatedAt'>;

        const newId = await createContent(newContent);
        const createdContent = { ...newContent, id: newId, createdAt: now, updatedAt: now };
        setContent(prev => [...prev, createdContent]);
        setSuccess('Content created successfully!');
      } else {
        const updates = {
          ...editingContent,
          updatedBy: userId,
        } as Partial<CMSContent>;

        await updateContent(editingContent.id!, updates);
        setContent(prev => prev.map(item => 
          item.id === editingContent.id 
            ? { ...item, ...updates, updatedAt: now }
            : item
        ));
        setSuccess('Content updated successfully!');
      }

      setEditDialogOpen(false);
      setEditingContent(null);
      setIsNewContent(false);

    } catch (err: any) {
      console.error('Error saving content:', err);
      setError(err.message || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item: CMSContent) => {
    try {
      const updates = { isActive: !item.isActive };
      await updateContent(item.id, updates);
      setContent(prev => prev.map(content => 
        content.id === item.id 
          ? { ...content, ...updates }
          : content
      ));
    } catch (err: any) {
      console.error('Error updating content status:', err);
      setError(err.message || 'Failed to update content status');
    }
  };

  const getUniqueCategories = () => {
    const categories = [...new Set(content.map(item => item.category).filter(Boolean))];
    return categories.sort();
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'text':
        return 'default';
      case 'html':
        return 'primary';
      case 'image':
        return 'secondary';
      case 'video':
        return 'warning';
      case 'json':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              <ContentIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              Content Blocks
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage reusable content blocks for your website
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddContent}
          >
            Add Content Block
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {getUniqueCategories().map(category => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="html">HTML</MenuItem>
                    <MenuItem value="image">Image</MenuItem>
                    <MenuItem value="video">Video</MenuItem>
                    <MenuItem value="json">JSON</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Content Table */}
        <Paper>
          {filteredContent.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ContentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography color="textSecondary" gutterBottom>
                {searchTerm || categoryFilter !== 'all' || typeFilter !== 'all' 
                  ? 'No content found matching your filters'
                  : 'No content blocks created yet'
                }
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Content blocks are reusable pieces of content that can be used across your website
              </Typography>
              <Button
                variant="contained"
                onClick={handleAddContent}
                startIcon={<AddIcon />}
              >
                Create Your First Content Block
              </Button>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Key</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredContent
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Typography variant="subtitle2">
                              {item.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.content.substring(0, 100)}
                              {item.content.length > 100 ? '...' : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {item.key}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.type}
                              color={getTypeColor(item.type) as any}
                              size="small"
                              sx={{ textTransform: 'uppercase' }}
                            />
                          </TableCell>
                          <TableCell>
                            {item.category ? (
                              <Chip label={item.category} size="small" variant="outlined" />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Uncategorized
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleActive(item)}
                              color={item.isActive ? 'success' : 'default'}
                            >
                              {item.isActive ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(item.updatedAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleEditContent(item)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(item)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredContent.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Paper>

        {/* Edit Dialog */}
        <Dialog 
          open={editDialogOpen} 
          onClose={() => setEditDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {isNewContent ? 'Add Content Block' : 'Edit Content Block'}
          </DialogTitle>
          <DialogContent>
            {editingContent && (
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Title"
                      value={editingContent.title || ''}
                      onChange={(e) => setEditingContent({ ...editingContent, title: e.target.value })}
                      sx={{ mb: 3 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Key"
                      value={editingContent.key || ''}
                      onChange={(e) => setEditingContent({ ...editingContent, key: e.target.value })}
                      helperText="Unique identifier for this content block"
                      sx={{ mb: 3 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={editingContent.type || 'text'}
                        onChange={(e) => setEditingContent({ ...editingContent, type: e.target.value as any })}
                        label="Type"
                      >
                        <MenuItem value="text">Text</MenuItem>
                        <MenuItem value="html">HTML</MenuItem>
                        <MenuItem value="image">Image</MenuItem>
                        <MenuItem value="video">Video</MenuItem>
                        <MenuItem value="json">JSON</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Category"
                      value={editingContent.category || ''}
                      onChange={(e) => setEditingContent({ ...editingContent, category: e.target.value })}
                      helperText="Optional category for organization"
                      sx={{ mb: 3 }}
                    />
                  </Grid>
                </Grid>

                <Typography variant="subtitle2" gutterBottom>
                  Content
                </Typography>
                {editingContent.type === 'html' ? (
                  <ReactQuill
                    value={editingContent.content || ''}
                    onChange={(content) => setEditingContent({ ...editingContent, content })}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        ['link', 'image'],
                        ['clean']
                      ],
                    }}
                    style={{ height: '200px', marginBottom: '50px' }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={editingContent.content || ''}
                    onChange={(e) => setEditingContent({ ...editingContent, content: e.target.value })}
                    placeholder={
                      editingContent.type === 'json' 
                        ? '{"key": "value"}' 
                        : 'Enter your content here...'
                    }
                  />
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveContent} 
              variant="contained"
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} /> : (isNewContent ? 'Create' : 'Save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Content Block</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the content block "{editingContent?.title}"? 
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default ContentPage; 